import React, { useState, useEffect } from "react";

import ReusableTable from "components/Tables/ReusableTable";

import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";

import {
    EllipsisVertical,
    Pencil,
    EyeIcon,
    TrendingUp,
    FileBadge,
} from "lucide-react";

import ViewAgent from "./components/ViewAgent";
import EditAgentDrawer from "./components/EditAgentDrawer";
import ViewAgentProgression from "./components/ViewAgentProgression";

import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import {
    fetchAgentCertificationsAction,
} from "../../../state/actions/agentCertificationActions";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchAgentsAction,
} from "../../../state/actions/agentActions";

import {
    formatDate,
} from "utils/dateFormatter";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";


const AgentManagement = () => {

    const [
        isViewAgentDrawerOpen,
        setViewAgentDrawerOpen,
    ] = useState(false);

    const [
        isEditAgentDrawerOpen,
        setEditAgentDrawerOpen,
    ] = useState(false);

    const [
    isViewAgentProgressionDrawerOpen,
    setViewAgentProgressionDrawerOpen,
] = useState(false);

    const [
        selectedAgent,
        setSelectedAgent,
    ] = useState(null);

   
    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);

    const PAGE_SIZE = 20;


    const [
        searchField,
        setSearchField,
    ] = useState(null);

    const [
        searchValue,
        setSearchValue,
    ] = useState("");

    const [
        searchTerm,
        setSearchTerm,
    ] = useState("");



    const dispatch = useDispatch();

    const {
        agents,
        loading,
        error,
        hasMore,
        total,
    } = useSelector(
        (state) => state.agents
    );


    const handleViewAgentProgression = (
    agent
) => {
    setSelectedAgent(agent);

    setViewAgentProgressionDrawerOpen(
        true
    );
};

const handleCloseViewAgentProgressionDrawer =
    () => {
        setSelectedAgent(null);

        setViewAgentProgressionDrawerOpen(
            false
        );
    };

    const handleSearch = (
        field,
        value
    ) => {

        setCurrentPage(1);

        setSearchField(field);

        setSearchTerm(value);
    };


    useEffect(() => {

        const timer = setTimeout(() => {

            setSearchValue(
                searchTerm
            );

        }, 500);

        return () =>
            clearTimeout(timer);

    }, [
        searchTerm,
    ]);



    useEffect(() => {

        dispatch(
            fetchAgentsAction(
                currentPage,
                PAGE_SIZE,
                searchField,
                searchValue
            )
        );

    }, [
        dispatch,
        currentPage,
        searchField,
        searchValue,
    ]);



    const handleViewAgent = (
        agent
    ) => {

        setSelectedAgent(
            agent
        );

        setViewAgentDrawerOpen(
            true
        );
    };


    const handleCloseViewAgentDrawer =
        () => {

            setSelectedAgent(
                null
            );

            setViewAgentDrawerOpen(
                false
            );
        };



    const handleOpenEditAgentDrawer =
        (agent) => {

            setSelectedAgent(
                agent
            );

            setEditAgentDrawerOpen(
                true
            );
        };


    const handleCloseEditAgentDrawer =
        () => {

            setSelectedAgent(
                null
            );

            setEditAgentDrawerOpen(
                false
            );
        };


        const handleViewCertificate = async (agent) => {
    const agentId = agent?.agent?.agent_id;

    if (!agentId) {
        return;
    }

    const result = await dispatch(
        fetchAgentCertificationsAction(agentId)
    );

    if (
        result.success &&
        result.data &&
        !Array.isArray(result.data)
    ) {
        const certificateNumber =
            result.data.certificate_number;

        if (certificateNumber) {
            const certificateUrl =
                `https://api.mogomabw.com/uploads/agents_certificates/${certificateNumber}.pdf`;

            window.open(
                certificateUrl,
                "_blank",
                "noopener,noreferrer"
            );
        }
    }
};


    const generateMenuItems = (
        row
    ) => [

        {
            icon:
                <EyeIcon
                    size={16}
                />,

            text:
                "View Agent",

            onClick: () =>
                handleViewAgent(
                    row.original
                ),
        },
         {
        icon: (
            <TrendingUp
                size={16}
            />
        ),

        text: "View Agent Progress",

        onClick: () =>
            handleViewAgentProgression(
                row.original
            ),
    },
    {
    icon: (
        <FileBadge
            size={16}
        />
    ),

    text: "View Certificate",

    onClick: () =>
        handleViewCertificate(
            row.original
        ),
},

        {
            icon:
                <Pencil
                    size={16}
                />,

            text:
                "Update Agent Status",

            onClick: () =>
                handleOpenEditAgentDrawer(
                    row.original
                ),
        },

    ];



    const columnsData = [

        {
            Header:
                "Agent Name",

            accessor:
                (row) => {

                    const firstName =
                        row.user
                            ?.first_name ||
                        "";

                    const lastName =
                        row.user
                            ?.last_name ||
                        "";

                    return (
                        `${firstName} ${lastName}`.trim() ||
                        row.user?.username ||
                        "N/A"
                    );
                },
        },


        {
            Header:
                "Agent Number",

            accessor:
                (row) =>
                    row.agent
                        ?.agent_number ||
                    "N/A",
        },


        {
            Header:
                "Phone Number",

            accessor:
                (row) =>
                    row.user
                        ?.phone_number ||
                    "N/A",
        },


        {
            Header:
                "Operating Area",

            accessor:
                (row) =>
                    row.agent
                        ?.operating_area ||
                    "N/A",
        },


        {
            Header:
                "Status",

            accessor:
                (row) =>
                    row.agent
                        ?.status ||
                    "Unknown",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={value}
                        variant={
                            getStatusVariant(
                                value
                            )
                        }
                    />
                ),
        },


        {
            Header:
                "Availability",

            accessor:
                (row) =>
                    row.agent
                        ?.availability_status ||
                    "N/A",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={value}
                        variant={
                            value === "Open"
                                ? "success"
                                : "gray"
                        }
                    />
                ),
        },


        {
            Header:
                "Created",

            accessor:
                (row) =>
                    row.agent
                        ?.created_at,

            Cell:
                ({
                    value,
                }) =>
                    value
                        ? formatDate(value)
                        : "N/A",
        },


        {
            Header:
                "Actions",

            accessor:
                "moreOptions",

            Cell:
                ({
                    row,
                }) => (

                    <ThreeDotMenu

                        icon={
                            <EllipsisVertical
                                size={20}
                                color="grey"
                            />
                        }

                        items={
                            generateMenuItems(
                                row
                            )
                        }

                    />
                ),
        },

    ];



    return (

        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >

            {/* HEADER */}

            <Box p={6}>

                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >

                    <Text
                        fontSize="md"
                        fontWeight="bold"
                    >

                        {loading ? (

                            <Skeleton
                                height="20px"
                                width="100px"
                            />

                        ) : (

                            `All Agents: ${
                                total ??
                                agents?.total ??
                                0
                            }`

                        )}

                    </Text>

                </Flex>

            </Box>


            {/* TABLE */}

            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    Array.isArray(
                        agents?.data
                    )
                        ? agents.data
                        : []
                }

                isLoading={
                    loading
                }

                error={
                    error
                }

                onSearch={
                    handleSearch
                }

                emptyMessage={
                    "No agents found. Try adjusting your search or filters."
                }

            />


            {/* PAGINATION */}

            <Box
                mt={4}
                display="flex"
                justifyContent="center"
                gap={2}
            >

                <GradientButton

                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                Math.max(
                                    prev - 1,
                                    1
                                )
                        )
                    }

                    disabled={
                        currentPage === 1
                    }

                    height="30px"
                >
                    Previous
                </GradientButton>


                <Text
                    alignSelf="center"
                >
                    Page{" "}
                    {
                        currentPage
                    }
                </Text>


                <GradientButton

                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                prev + 1
                        )
                    }

                    disabled={
                        !hasMore
                    }

                    height="30px"
                >
                    Next
                </GradientButton>

            </Box>


            <ReusableDrawer

                isOpen={
                    isViewAgentDrawerOpen
                }

                onClose={
                    handleCloseViewAgentDrawer
                }

                headerContent={
                    <h2
                        style={{
                            fontSize: "md",
                        }}
                    >
                        View Agent
                    </h2>
                }

                bodyContent={

                    selectedAgent && (

                        <ViewAgent
                            agent={
                                selectedAgent
                            }

                            onClose={
                                handleCloseViewAgentDrawer
                            }
                        />

                    )

                }

            />

            <ReusableDrawer
    isOpen={
        isViewAgentProgressionDrawerOpen
    }
    onClose={
        handleCloseViewAgentProgressionDrawer
    }
    headerContent={
        <Flex
            alignItems="center"
            gap={3}
        >
            <TrendingUp
                size={20}
            />

            <Text
                fontSize="md"
                fontWeight="bold"
            >
                Agent Progress
            </Text>
        </Flex>
    }
    bodyContent={
        selectedAgent && (
            <ViewAgentProgression
                agent={
                    selectedAgent
                }
                onClose={
                    handleCloseViewAgentProgressionDrawer
                }
            />
        )
    }
/>
            <EditAgentDrawer

                isOpen={
                    isEditAgentDrawerOpen
                }

                onClose={
                    handleCloseEditAgentDrawer
                }

                agent={
                    selectedAgent
                }

            />

        </Flex>
    );
};


export default AgentManagement;